import { Stack } from "@mui/material";
import {
  Dialog,
  FoundationColorTokens,
  Spacing,
  TextInputField,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useEffect, useState } from "react";
import { DialogErrorContent } from "../../../../components/DialogErrorContent";
import {
  RenameSurveyLanguageError,
  TranslationsStore,
} from "../../store/TranslationsStore";

interface RenameSurveyLanguageDialogProps {
  open: boolean;
  languageCode: string;
  surveyId: string;
  projectId: string;
  languageId: string;
  translationsStore: Instance<typeof TranslationsStore>;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  onClose: VoidFunction;
  onSuccess: () => Promise<void>;
}

const MAX_LANGUAGE_CODE_LENGTH = 128;

export const RenameSurveyLanguageDialog = observer(
  ({
    open,
    languageCode,
    surveyId,
    projectId,
    languageId,
    translationsStore,
    t,
    spacing,
    typography,
    tokens,
    onClose,
    onSuccess,
  }: RenameSurveyLanguageDialogProps): React.ReactElement => {
    const [updatedLanguageCode, setUpdatedLanguageCode] =
      useState(languageCode);

    useEffect(() => {
      if (open) {
        setUpdatedLanguageCode(languageCode);
        translationsStore.resetRenameSurveyLanguageState();
      }
    }, [open, languageCode]);

    const trimmedLanguageCode = updatedLanguageCode.trim();
    const isBlank = trimmedLanguageCode.length === 0;
    const isTooLong = trimmedLanguageCode.length > MAX_LANGUAGE_CODE_LENGTH;
    const isDuplicate =
      translationsStore.renameSurveyLanguageError ===
      RenameSurveyLanguageError.DuplicateLanguageCode;
    const isInputInvalid = isBlank || isTooLong || isDuplicate;
    const isNonFieldError =
      translationsStore.renameSurveyLanguageError !== null &&
      !isDuplicate &&
      translationsStore.renameSurveyLanguageError !==
        RenameSurveyLanguageError.InvalidLanguageCode;

    const getHelperText = (): string | undefined => {
      if (isBlank) {
        return t("surveys.translations.renameDialog.blankError");
      }
      if (isTooLong) {
        return t("surveys.translations.renameDialog.maximumLengthError");
      }
      if (isDuplicate) {
        return t("surveys.translations.renameDialog.duplicateError");
      }
      return undefined;
    };

    const getErrorText = (): string => {
      switch (translationsStore.renameSurveyLanguageError) {
        case RenameSurveyLanguageError.ProjectAlreadyArchived:
          return t("surveys.translations.languageAction.projectArchivedError");
        case RenameSurveyLanguageError.TranslationUpdateInProgress:
          return t(
            "surveys.translations.languageAction.translationUpdateInProgressError",
          );
        default:
          return t("surveys.translations.languageAction.unexpectedError");
      }
    };

    const closeDialog = (): void => {
      translationsStore.resetRenameSurveyLanguageState();
      onClose();
    };

    return (
      <Dialog
        open={open}
        title={t("surveys.translations.renameDialog.title")}
        primaryButtonText={
          isNonFieldError
            ? undefined
            : t("surveys.translations.renameDialog.title")
        }
        onPrimaryButtonClick={async () => {
          if (translationsStore.isRenamingSurveyLanguage) {
            return;
          }
          await translationsStore.renameSurveyLanguage(
            surveyId,
            projectId,
            languageId,
            trimmedLanguageCode,
          );
          if (translationsStore.renameSurveyLanguageError === null) {
            await onSuccess();
            closeDialog();
          }
        }}
        isPrimaryButtonDisabled={isInputInvalid}
        secondaryButtonText={
          isNonFieldError ? t("common.close") : t("common.cancel")
        }
        onSecondaryButtonClick={closeDialog}
        isSecondaryButtonDisabled={translationsStore.isRenamingSurveyLanguage}
        disableBackdropClick={true}
        contentPadding={spacing.spaceLG}
        width="560px"
      >
        {isNonFieldError ? (
          <DialogErrorContent
            spacing={spacing}
            tokens={tokens}
            typography={typography}
            errorText={getErrorText()}
          />
        ) : (
          <Stack gap={spacing.spaceLG} width="100%">
            <TextInputField
              name="surveyLanguageCode"
              value={updatedLanguageCode}
              required={true}
              type="text"
              onTextChange={(value) => {
                setUpdatedLanguageCode(value);
                translationsStore.resetRenameSurveyLanguageState();
              }}
              label={t("surveys.translations.renameDialog.inputLabel")}
              placeholder={t(
                "surveys.translations.renameDialog.inputPlaceholder",
              )}
              error={isInputInvalid}
              helperText={getHelperText()}
              maxCharacterLength={MAX_LANGUAGE_CODE_LENGTH}
              disabled={translationsStore.isRenamingSurveyLanguage}
              showCharacterCountIndicator={() => false}
              width="70%"
            />
          </Stack>
        )}
      </Dialog>
    );
  },
);
